import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  forceNotificationAction,
  removeNotification,
  selectForceNotification,
  selectNotifications,
  selectNotificationsIsLoading,
} from "../../auth/data-access/store/authSlice";
import { LevelUp } from "../../shared/ui/modal-notifications/level-up";
import { Notification } from "../../shared/interfaces/auth.types";
import { selectLanguage } from "../../shared/data-access/store/general/generalSlice";
import { useConsumeNotificationsMutation } from "../../auth/data-access/store/services/auth.service";
import { BadgeModal } from "../../badges/ui/achievement-modal";
import { Modal } from "../../shared/ui/modal";
import PlayBonusGame from "../../shared/ui/modal-notifications/play-bonus-game/play-bonus-game";
import { useLocation, useNavigate } from "react-router-dom";
import SoundManager from "../../utils/managers/sound-manager";

export const Notifications = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectNotifications);
  const [showNotification, setShowNotification] = useState(false);
  const [isConsumingNotification, setIsConsumingNotification] = useState(false);
  const lang = useAppSelector(selectLanguage);
  const [consumeNotifications] = useConsumeNotificationsMutation();
  const { pathname } = useLocation();
  const forceNotification = useAppSelector(selectForceNotification);
  const notificationsIsLoading = useAppSelector(selectNotificationsIsLoading);

  const priority = {
    achievement: 3,
    levelUp: 2,
    bonusGame: 1,
  };
  const SUPPORTED_TYPES = ["bonusGame", "levelUp", "achievement"];
  const notificationsCopy =
    notifications && notifications.length > 0
      ? notifications.filter((n) => SUPPORTED_TYPES.includes(n.type))
      : null;
  const sortedNotifications = notificationsCopy?.sort(
    (a, b) => priority[b.type] - priority[a.type]
  );

  const handleConsumeNotification = (nodificationId: string) => {
    setShowNotification(false);
    setIsConsumingNotification(true);
    if (sortedNotifications && sortedNotifications.length > 0) {
      consumeNotifications({ id: nodificationId });
      dispatch(removeNotification({ id: nodificationId }));
    }
    if (forceNotification.show) {
      dispatch(forceNotificationAction({ show: false, id: null }));
    }
    // Reset the consuming flag after a short delay to prevent auto-show of next notification
    setTimeout(() => {
      setIsConsumingNotification(false);
    }, 500);
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    // Don't auto-show notifications while one is being consumed
    if (
      !isConsumingNotification &&
      ((sortedNotifications &&
        sortedNotifications.length &&
        !checkIfInGame(pathname)) ||
        forceNotification.show)
    ) {
      timeout = setTimeout(() => {
        if (!notificationsIsLoading) {
          setShowNotification(true);
        }
      }, 400);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    sortedNotifications,
    consumeNotifications,
    pathname,
    forceNotification,
    notificationsIsLoading,
    isConsumingNotification,
  ]);

  const checkIfInGame = (path: string) => {
    return (
      path.includes("opponent") ||
      path.includes("ladder") ||
      path.includes("gameplay") ||
      path.includes("category") ||
      path.includes("results")
    );
  };

  const renderNotificationByType = (notification: Notification) => {
    switch (notification.type) {
      case "levelUp":
        return (
          <LevelUp
            modalOpen={
              forceNotification.show
                ? forceNotification.id === notification.id
                : showNotification
            }
            setModalOpen={setShowNotification}
            buttonClick={() => {
              handleConsumeNotification(notification.id);
              SoundManager.ambientSounds.home.volume = 0.6;
              SoundManager.ambientSounds.home.play();
            }}
            level={notification.text?.new_level}
            title={notification.text?.level_up[lang]}
            subtitle={notification.text?.awesome[lang]}
            lives={notification.text?.amount_lives_awarded}
            livesText={notification.text?.award_lives[lang]}
            nextLevelDescription={notification.text?.next_threshold[lang]}
            points={notification.text?.current_diamonds}
            buttonText={notification.button_text[lang]}
          />
        );
      case "bonusGame": {
        return (
          <PlayBonusGame
            modalOpen={
              forceNotification.show
                ? forceNotification.id === notification.id
                : showNotification
            }
            setModalOpen={setShowNotification}
            title={notification.text?.description[lang]}
            subtitle={notification.text?.title[lang]}
            description={notification.text?.hint[lang]}
            buttonText={notification.button_text[lang]}
            buttonClick={() => {
              handleConsumeNotification(notification.id);
              navigate(`/gameplay/bonus?id=${notification.target_id}`);
            }}
          ></PlayBonusGame>
        );
      }
      case "achievement": {
        return (
          <Modal
            show={
              forceNotification.show
                ? forceNotification.id === notification.id
                : showNotification
            }
            setShow={setShowNotification}
            hideCloseButton
            disableClickOutside={true}
          >
            <BadgeModal
              badge={notification.image}
              title={notification.text?.title[lang]}
              buttonText={notification.button_text[lang]}
              buttonClick={() => {
                handleConsumeNotification(notification.id);
              }}
            >
              {notification.text?.description[lang]}
            </BadgeModal>
          </Modal>
        );
      }
    }
  };

  return (
    <>
      {sortedNotifications && sortedNotifications.length > 0
        ? renderNotificationByType(
            forceNotification.show
              ? (sortedNotifications.find(
                  (notification) => notification.id === forceNotification.id
                ) as Notification)
              : sortedNotifications[0]
          )
        : null}
    </>
  );
};

export default Notifications;
